<template>
	<div v-if="loaded">
		<div
			:class="[
				'c-job-opportunity-filters',
				{ 'is-active': filtersActive },
			]">
			<div class="c-job-opportunity-filters__control">
				<h3>Filters</h3>
				<button
					class="c-job-opportunity-filters__close iconf-close"
					aria-label="Close Filter"
					@click="filtersActive = false"></button>
			</div>

			<div
				v-if="filters.departments"
				class="c-job-opportunity-filter">
				<button
					type="button"
					:class="[
						'c-job-opportunity-filter__heading',
						{ 'is-showing': showing.departments },
					]"
					@click="toggleShowing('departments')">
					Departments
				</button>
				<ul class="c-job-opportunity-filter__list">
					<p>Select departments</p>
					<li
						class="c-job-opportunity-filter__list-item"
						v-for="dept in filters.departments"
						:key="dept.id">
						<label>
							<input
								type="checkbox"
								:value="dept.name"
								:key="dept.id"
								v-model="selectedDepartments" />
							{{ dept.name }}
						</label>
					</li>
				</ul>
			</div>

			<div
				v-if="filters.jobTypes"
				class="c-job-opportunity-filter">
				<button
					type="button"
					:class="[
						'c-job-opportunity-filter__heading',
						{ 'is-showing': showing.jobTypes },
					]"
					@click="toggleShowing('jobTypes')">
					Job Types
				</button>
				<ul class="c-job-opportunity-filter__list">
					<p>Select job types</p>

					<li
						class="c-job-opportunity-filter__list-item"
						v-for="jobType in filters.jobTypes"
						:key="jobType.id">
						<label>
							<input
								type="checkbox"
								:value="jobType.name"
								:key="jobType.id"
								v-model="selectedJobTypes" />
							{{ jobType.name }}
						</label>
					</li>
				</ul>
			</div>

			<div
				v-if="filters.statuses"
				class="c-job-opportunity-filter">
				<button
					type="button"
					:class="[
						'c-job-opportunity-filter__heading',
						{ 'is-showing': showing.statuses },
					]"
					@click="toggleShowing('statuses')">
					Status
				</button>
				<ul class="c-job-opportunity-filter__list">
					<p>Select opportunity status</p>
					<li
						class="c-job-opportunity-filter__list-item"
						v-for="status in filters.statuses"
						:key="status.id">
						<label>
							<input
								type="checkbox"
								:value="status.id"
								:key="status.id"
								v-model="selectedStatuses" />
							{{ status.name }}
						</label>
					</li>
				</ul>
			</div>

			<div class="c-job-opportunity-filter">
				<div class="c-job-opportunity-filter__search">
					<span
						class="c-job-opportunity-filter__search-button iconf-search"
						aria-label="Search"></span>
					<input
						class="c-job-opportunity-filter__search-input"
						type="text"
						name="name"
						v-model="searchKeyword"
						placeholder="Search" />
				</div>
			</div>
		</div>

		<div
			class="c-job-opportunity-filters-active"
			v-if="hasActiveFilters">
			<div class="c-job-opportunity-list__layout">
				<button
					v-for="dept in selectedDepartments"
					:key="'dept-' + dept"
					class="c-job-opportunity-filters-active__item"
					type="button"
					@click="removeDepartment(dept)">
					{{ dept }}
				</button>
				<button
					v-for="jobType in selectedJobTypes"
					:key="'jobType-' + jobType"
					class="c-job-opportunity-filters-active__item"
					type="button"
					@click="removeJobType(jobType)">
					{{ jobType }}
				</button>
				<button
					v-for="status in selectedStatuses"
					:key="'status-' + status"
					class="c-job-opportunity-filters-active__item"
					type="button"
					@click="removeStatus(status)">
					{{ status }}
				</button>
				<button
					class="c-job-opportunity-filters-active__item-reset"
					type="reset"
					@click="resetAllFilters"
					aria-label="Reset all filters">
					Clear All Filters
				</button>
			</div>
		</div>

		<div class="c-opportunities">
			<div class="c-job-opportunity-list__layout">
				<button
					type="button"
					class="c-job-opportunity-filters__open"
					:aria-label="filterToggleAriaLabel"
					@click="filtersActive = !filtersActive">
					{{ filterToggleText }}
				</button>

				<ul
					v-if="filteredOpportunities.length"
					class="c-job-opportunity-list__listing">
					<li
						class="c-job-opportunity-list-item"
						v-for="(opportunity, idx) in filteredOpportunities"
						:key="opportunity.key || idx"
						:data-filter-department="
							opportunity.departmentType
								? opportunity.departmentType.name
								: ''
						"
						:data-filter-jobtype="
							opportunity.jobType
								? opportunity.jobType.name
								: ''
						"
						:data-filter-status="
							opportunity.status ? opportunity.status.name.id : ''
						"
						:data-filter-opendate="opportunity.openDate"
						:data-filter-closedate="opportunity.closeDate">
						<a
							class="c-job-opportunity-list-item__link"
							:href="opportunity.url">
							<div
								class="c-job-opportunity-list-item__block c-job-opportunity-list-item__block--icon">
								<span
									:class="[
										'c-job-opportunity-list-item__icon',
										opportunity.icon,
									]"></span>
							</div>
							<div
								class="c-job-opportunity-list-item__block c-job-opportunity-list-item__block--info">
								<span
									v-if="opportunity.jobType"
									class="c-job-opportunity-list-item__code">
									{{
										opportunity.jobType
											? opportunity.jobType.name
											: ''
									}}
								</span>
								<span
									v-if="opportunity.referenceNumber"
									class="c-job-opportunity-list-item__ref"
									>{{ opportunity.referenceNumber }}</span
								>
								<span
									v-if="opportunity.departmentType"
									class="c-job-opportunity-list-item__category">
									{{
										opportunity.departmentType
											? opportunity.departmentType.name
											: ''
									}}
								</span>
							</div>
							<div class="c-job-opportunity-list-item__block">
								<h3
									v-if="opportunity.title"
									class="c-job-opportunity-list-item__title">
									{{ opportunity.title }}
								</h3>
								<p
									v-if="opportunity.abstract"
									class="c-job-opportunity-list-item__desc">
									{{ opportunity.abstract }}
								</p>
							</div>
							<div class="c-job-opportunity-list-item__block">
								<span
									:class="[
										'c-job-opportunity-list-item__status',
										opportunity.status
											? 'status-' + opportunity.status.id
											: '',
									]">
									{{
										opportunity.status
											? opportunity.status.name
											: ''
									}}
								</span>
								<div
									v-if="opportunity.closeDate"
									class="c-job-opportunity-list-item__closing">
									Closing Date:
									<span>{{
										formatOpportunityDate(
											opportunity.closeDate,
										)
									}}</span>
								</div>
							</div>
							<div
								class="c-job-opportunity-list-item__block c-job-opportunity-list-item__block--button">
								<div
									class="c-job-opportunity-list-item__button">
									View
								</div>
							</div>
						</a>
					</li>
				</ul>
				<p v-else>
					<br />
					There are no opportunities matching your filter(s).
				</p>
			</div>
		</div>
	</div>
</template>

<script>
import info from 'Vue/job-opportunities-data';
export default {
	name: 'jobOpportunityListing',
	data() {
		return {
			loaded: false,
			opportunities: null,
			selectedDepartments: [],
			selectedJobTypes: [],
			selectedStatuses: [],
			filtersActive: false,
			filters: {},
			showing: {
				departments: false,
				jobTypes: false,
				statuses: false,
			},
			searchKeyword: '',
		};
	},
	mounted() {
		// Fetch the opportunities data from the API
		fetch(info.api)
			.then((res) => res.json())
			.then((response) => {
				this.opportunities = response.jobOpportunities || [];
				this.filters.departments =
					response.activeDepartmentFilterOptions || [];
				this.filters.jobTypes =
					response.activeJobTypeFilterOptions || [];
				this.filters.statuses = response.statusOptions || [];
				this.loaded = true;
			})
			.catch((error) => {
				console.error('API Error:', error);
				this.opportunities = [];
			});

		document.addEventListener('click', this.handleClickOutside);
	},
	beforeUnmount() {
		document.removeEventListener('click', this.handleClickOutside);
	},
	computed: {
		filteredOpportunities() {
			const filtered = this.opportunities.filter((opportunity) => {
				// Department filter
				const allDepartmentsSelected =
					this.selectedDepartments.length === 0 ||
					this.selectedDepartments.includes('');
				const departmentMatches =
					allDepartmentsSelected ||
					(opportunity.departmentType &&
						this.selectedDepartments.includes(
							opportunity.departmentType.name,
						));

				// Job type filter
				const allJobTypesSelected =
					this.selectedJobTypes.length === 0 ||
					this.selectedJobTypes.includes('');
				const jobTypeMatches =
					allJobTypesSelected ||
					(opportunity.jobType &&
						this.selectedJobTypes.includes(
							opportunity.jobType.name,
						));

				// Status filter
				const statusId = opportunity.status?.id;
				const allStatusesSelected =
					this.selectedStatuses.length === 0 ||
					this.selectedStatuses.includes('');
				const statusMatches =
					allStatusesSelected ||
					(statusId && this.selectedStatuses.includes(statusId));

				// Keyword search
				const keyword = this.searchKeyword.trim().toLowerCase();
				const matchesSearch =
					!keyword ||
					(opportunity.title &&
						opportunity.title.toLowerCase().includes(keyword)) ||
					(opportunity.departmentType &&
						opportunity.departmentType.name.toLowerCase().includes(keyword)) ||
					(opportunity.jobType &&
					opportunity.jobType.name.toLowerCase().includes(keyword)) ||
					(opportunity.referenceNumber &&
						opportunity.referenceNumber
							.toLowerCase()
							.includes(keyword));

				return (
					departmentMatches &&
					jobTypeMatches &&
					statusMatches &&
					matchesSearch
				);
			});

			// Sort: items with closeDate first, oldest to newest
			return filtered.sort((a, b) => {
				const closeDateA = a.closeDate ? new Date(a.closeDate) : null;
				const closeDateB = b.closeDate ? new Date(b.closeDate) : null;

				if (closeDateA && closeDateB) return closeDateA - closeDateB; // oldest to newest
				if (closeDateA && !closeDateB) return -1; // a first if it has date
				if (!closeDateA && closeDateB) return 1; // b first if it has date
				return 0; // both have no date
			});
		},
		hasActiveFilters() {
			return (
				this.selectedDepartments.length > 0 ||
				this.selectedJobTypes.length > 0 ||
				this.selectedStatuses.length > 0
			);
		},

		filterToggleText() {
			return this.filtersActive ? 'Close Filters' : 'Open Filters';
		},
		filterToggleAriaLabel() {
			return this.filtersActive ? 'Close Filter' : 'Open Filter';
		},
	},
	methods: {
		removeDepartment(dept) {
			this.selectedDepartments = this.selectedDepartments.filter(
				(d) => d !== dept,
			);
		},
		removeJobType(jobType) {
			this.selectedJobTypes = this.selectedJobTypes.filter(
				(t) => t !== jobType,
			);
		},
		removeStatus(status) {
			this.selectedStatuses = this.selectedStatuses.filter(
				(s) => s !== status,
			);
		},
		toggleShowing(group) {
			// Close all groups except the one clicked
			for (const key in this.showing) {
				this.showing[key] = key === group ? !this.showing[key] : false;
			}
		},
		handleClickOutside(event) {
			// Check if click happened outside of .c-job-opportunity-filter
			const clickedInside = event.target.closest(
				'.c-job-opportunity-filter',
			);
			if (!clickedInside) {
				for (const key in this.showing) {
					this.showing[key] = false;
				}
			}
		},
		resetAllFilters() {
			this.selectedDepartments = [];
			this.selectedJobTypes = [];
			this.selectedStatuses = [];
			this.searchKeyword = '';
		},
		formatOpportunityDate(dateStr) {
			if (!dateStr) return '';
			const date = new Date(dateStr);
			if (isNaN(date)) return dateStr;

			let hours = date.getHours();
			const minutes = date.getMinutes();
			const ampm = hours >= 12 ? 'pm' : 'am';
			hours = hours % 12 || 12;

			const hoursStr = String(hours).padStart(2, '0');
			const minutesStr = String(minutes).padStart(2, '0');

			const day = date.getDate();
			const monthNames = [
				'Jan',
				'Feb',
				'Mar',
				'Apr',
				'May',
				'Jun',
				'Jul',
				'Aug',
				'Sept',
				'Oct',
				'Nov',
				'Dec',
			];
			const month = monthNames[date.getMonth()];
			const year = date.getFullYear();

			return `${hoursStr}:${minutesStr} ${ampm}, ${day} ${month} ${year}`;
		},
	},
};
</script>
